import React from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from 'antd';
import { RollbackOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { EligibleItem } from '../../services/m5m8Api';
import { useM5M8Reversal } from './hooks/useM5M8Reversal';
import './M5M8Form.css';

const { Title, Text } = Typography;

function renderStatus(status: string): React.ReactNode {
  if (status === 'P') return <Tag color="blue">已核准</Tag>;
  if (status === 'N') return <Tag color="red">已沖銷</Tag>;
  return <Tag color="default">{status || '未知'}</Tag>;
}

const M5M8Form: React.FC = () => {
  const {
    loading,
    executing,
    items,
    total,
    selectedKeys,
    filter,
    setFilter,
    setSelectedKeys,
    fetchEligible,
    batchExecute,
  } = useM5M8Reversal();

  const columns: ColumnsType<EligibleItem> = [
    {
      title: '交運單號',
      dataIndex: 'fm160_01',
      key: 'fm160_01',
      width: 110,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: '交運日期',
      dataIndex: 'fm160_11',
      key: 'fm160_11',
      width: 110,
      render: (date: string) => date || '\u2014',
    },
    {
      title: '客戶代碼',
      dataIndex: 'fm160_22',
      key: 'fm160_22',
      width: 100,
    },
    {
      title: '客戶名稱',
      dataIndex: 'cust_name',
      key: 'cust_name',
      ellipsis: true,
    },
    {
      title: '版本',
      dataIndex: 'fm160_90',
      key: 'fm160_90',
      width: 60,
      align: 'center',
    },
    {
      title: '狀態',
      dataIndex: 'fm160_80',
      key: 'fm160_80',
      width: 90,
      align: 'center',
      render: renderStatus,
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
  };

  const handleSearch = () => { fetchEligible(); };

  const handleFilterChange = (field: 'startNo' | 'endNo', value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  const handleStartBlur = () => {
    if (filter.startNo && !filter.endNo) {
      const autoEnd = (filter.startNo + 'zzzzzzzz').substring(0, 7);
      setFilter({ ...filter, endNo: autoEnd });
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ marginBottom: 16 }}>客戶寄庫交運單沖銷 (pwm5m8f1)</Title>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label="單號起始">
            <Input
              value={filter.startNo}
              onChange={(e) => handleFilterChange('startNo', e.target.value)}
              onBlur={handleStartBlur}
              placeholder="例：FM60001"
              style={{ width: 120 }}
              maxLength={7}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item label="單號結束">
            <Input
              value={filter.endNo}
              onChange={(e) => handleFilterChange('endNo', e.target.value)}
              placeholder="例：FM69999"
              style={{ width: 120 }}
              maxLength={7}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
            >
              查詢
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">
                共 <strong>{total}</strong> 筆待沖銷
              </Text>
              {selectedKeys.length > 0 && (
                <Text type="secondary">
                  已勾選 <strong>{selectedKeys.length}</strong> 筆
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Popconfirm
              title={`確認沖銷 ${selectedKeys.length} 筆寄庫交運單？沖銷後狀態將設為 N，並回復庫存。`}
              onConfirm={batchExecute}
              okText="確認沖銷"
              cancelText="取消"
              disabled={selectedKeys.length === 0}
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<RollbackOutlined />}
                loading={executing}
                disabled={selectedKeys.length === 0}
              >
                批量沖銷
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      <Table<EligibleItem>
        rowKey="fm160_01"
        columns={columns}
        dataSource={items}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 筆`,
        }}
        scroll={{ x: 650 }}
        locale={{ emptyText: '無待沖銷資料' }}
      />
    </div>
  );
};

export default M5M8Form;
